/* assets/delivery-window.js
   HARD-CODED, performance-focused, Cart Drawer compatible.

   Requirements:
цвет:
   - Show only when snippet exists (Liquid already checks: cart not empty AND all products tagged "DHL")
   - Calculate delivery window in working days (Mon–Sat), excluding Sundays and holidays
   - Exclude holidays and Sundays from output dates
   - Output format: "Mi., 14.01.2026 - Do., 15.01.2026"
   - Centered styling is handled in CSS (inherits theme typography)
   - Must not slow down cart actions (debounced network, max 1 cart.js request at a time)
*/

(() => {
  if (window.__DW_BOOTED__) return;
  window.__DW_BOOTED__ = true;

  /* =========================
     HARD-CODED CONFIG
     ========================= */
  const CFG = {
    minBusinessDays: 2,
    maxBusinessDays: 4,
    prefix: 'Lieferung',
    includeDeNationalHolidays: true,
    // Additional holidays (YYYY-MM-DD):
    customHolidays: new Set([
      // '2026-01-06',
      // '2026-11-01'
    ])
  };

  /* =========================
     Date helpers
     ========================= */
  const pad2 = (n) => (n < 10 ? '0' + n : '' + n);
  const toYMD = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  const addDays = (dt, days) => {
    const d = new Date(dt);
    d.setDate(d.getDate() + days);
    return d;
  };

  // Output: "Mi., 14.01.2026"
  const formatDeliveryDate = (dt) => {
    const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(dt); // "Mi."
    const day = pad2(dt.getDate());
    const month = pad2(dt.getMonth() + 1);
    const year = dt.getFullYear();
    return `${weekday}, ${day}.${month}.${year}`;
  };

  // Easter Sunday (Gregorian)
  const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=Mar, 4=Apr
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // DE nationwide holidays cache (year -> Set(YYYY-MM-DD))
  const deHolidayCache = new Map();
  const buildDeHolidaySet = (year) => {
    const set = new Set([
      `${year}-01-01`,
      `${year}-05-01`,
      `${year}-10-03`,
      `${year}-12-25`,
      `${year}-12-26`
    ]);

    const easter = easterSunday(year);
    set.add(toYMD(addDays(easter, -2))); // Karfreitag
    set.add(toYMD(addDays(easter, 1)));  // Ostermontag
    set.add(toYMD(addDays(easter, 39))); // Christi Himmelfahrt
    set.add(toYMD(addDays(easter, 50))); // Pfingstmontag

    return set;
  };

  const isSunday = (dt) => dt.getDay() === 0;

  const isHoliday = (dt) => {
    const ymd = toYMD(dt);
    if (CFG.customHolidays.has(ymd)) return true;

    if (CFG.includeDeNationalHolidays) {
      const y = dt.getFullYear();
      let set = deHolidayCache.get(y);
      if (!set) {
        set = buildDeHolidaySet(y);
        deHolidayCache.set(y, set);
      }
      if (set.has(ymd)) return true;
    }
    return false;
  };

  // Working day: Mon–Sat excluding holidays
  const isWorkingDay = (dt) => !isSunday(dt) && !isHoliday(dt);

  const nextWorkingDay = (dt) => {
    let d = new Date(dt);
    while (!isWorkingDay(d)) d = addDays(d, 1);
    return d;
  };

  // Adds N working days starting from tomorrow
  const addWorkingDays = (start, n) => {
    let d = addDays(start, 1);
    let added = 0;

    while (added < n) {
      if (isWorkingDay(d)) added++;
      if (added < n) d = addDays(d, 1);
    }
    return nextWorkingDay(d);
  };

  /* =========================
     DOM update (NO NETWORK)
     ========================= */
  const applyToDom = () => {
    const nodes = document.querySelectorAll('[data-dw]');
    if (!nodes.length) return;

    const now = new Date();
    const from = addWorkingDays(now, CFG.minBusinessDays);
    const to = addWorkingDays(now, CFG.maxBusinessDays);

    const fromStr = formatDeliveryDate(from);
    const toStr = formatDeliveryDate(to);

    // Required schema: "Mi., 14.01.2026 - Do., 15.01.2026"
    const rangeHtml = `<strong>${fromStr}</strong> - <strong>${toStr}</strong>`;
    const html = `${CFG.prefix} ${rangeHtml}`;

    nodes.forEach((root) => {
      const textEl = root.querySelector('[data-dw-text]');
      if (textEl) textEl.innerHTML = html;
    });
  };

  /* =========================
     Visibility sync (NETWORK)
     - Debounced
     - Max 1 request at a time
     ========================= */
  let cartSyncTimer = null;
  let cartSyncInFlight = null;

  const syncVisibility = () => {
    const nodes = document.querySelectorAll('[data-dw]');
    if (!nodes.length) return;

    if (cartSyncInFlight) return;

    cartSyncInFlight = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then((r) => r.json())
      .then((cart) => {
        const hasItems = cart && cart.item_count > 0;

        // NOTE: Tag rule ("all products must have DHL") is enforced server-side by Liquid.
        // Here we only hide when cart becomes empty without drawer re-render.
        nodes.forEach((n) => {
          n.style.display = hasItems ? '' : 'none';
        });

        if (hasItems) applyToDom();
      })
      .catch(() => {
        // If request fails, don't harm UX; still render text
        applyToDom();
      })
      .finally(() => {
        cartSyncInFlight = null;
      });
  };

  const scheduleCartSync = () => {
    if (cartSyncTimer) return;
    cartSyncTimer = setTimeout(() => {
      cartSyncTimer = null;
      syncVisibility();
    }, 120);
  };

  /* =========================
     Debounced DOM apply
     ========================= */
  let domTimer = null;
  const scheduleDomApply = () => {
    if (domTimer) return;
    domTimer = setTimeout(() => {
      domTimer = null;
      requestAnimationFrame(applyToDom);
    }, 40);
  };

  /* =========================
     Detect cart endpoints
     ========================= */
  const isCartEndpoint = (url) => {
    if (!url) return false;
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  };

  /* =========================
     Hook fetch
     ========================= */
  if (!window.__DW_FETCH_PATCHED__) {
    window.__DW_FETCH_PATCHED__ = true;
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const p = origFetch.apply(this, arguments);
      try {
        const url =
          typeof input === 'string'
            ? input
            : input && input.url
            ? input.url
            : '';
        if (isCartEndpoint(url)) {
          p.finally(() => {
            scheduleDomApply();
            scheduleCartSync();
          });
        }
      } catch {}
      return p;
    };
  }

  /* =========================
     Hook XHR (many themes use it)
     ========================= */
  if (!window.__DW_XHR_PATCHED__) {
    window.__DW_XHR_PATCHED__ = true;
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__dw_url__ = url;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      try {
        const url = this.__dw_url__;
        if (isCartEndpoint(url)) {
          this.addEventListener(
            'loadend',
            () => {
              scheduleDomApply();
              scheduleCartSync();
            },
            { once: true }
          );
        }
      } catch {}
      return origSend.apply(this, arguments);
    };
  }

  /* =========================
     Observe ONLY cart drawer for DOM insertions
     (NO NETWORK here!)
     ========================= */
  const getDrawerRoot = () =>
    document.querySelector('#CartDrawer') ||
    document.querySelector('cart-drawer') ||
    document.querySelector('[data-cart-drawer]') ||
    document.querySelector('[id*="CartDrawer"]');

  const startDrawerObserver = () => {
    const drawer = getDrawerRoot();
    if (!drawer || drawer.__dw_observing__) return;
    drawer.__dw_observing__ = true;

    const mo = new MutationObserver(() => {
      // Drawer content changed -> only update text (cheap)
      scheduleDomApply();
    });

    mo.observe(drawer, { childList: true, subtree: true });
  };

  /* =========================
     Init
     ========================= */
  const init = () => {
    startDrawerObserver();

    // Initial pass (handles case where drawer HTML already contains the snippet)
    applyToDom();
    scheduleCartSync();

    // When clicking cart toggle, update again
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!t || !t.closest) return;

        if (
          t.closest('[href*="/cart"]') ||
          t.closest('[data-cart-drawer-open]') ||
          t.closest('[aria-controls*="CartDrawer"]')
        ) {
          scheduleDomApply();
          scheduleCartSync();
        }
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();